import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  Tag,
  Space,
  Select,
  Tabs,
  Modal,
  Typography,
  Avatar,
  Tooltip,
  Popconfirm,
  message,
  Badge,
  Card,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ExportOutlined,
  PhoneOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useRealtimeTicket } from '../contexts/RealtimeTicketContext';
import api from '../config/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import DateFilter, { DateFilterValue } from '../components/DateFilter';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  priority: string;
  category_name?: string;
  business_name: string;
  assigned_to_first_name?: string;
  assigned_to_last_name?: string;
  created_by_name?: string;
  created_by_last_name?: string;
  created_at: string;
  updated_at: string;
}

const getTabFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  if (tab === 'callme') return '6';
  if (tab === 'fromchat') return '7';
  if (tab === 'unassigned') return '5';
  if (tab === 'open') return '2';
  if (tab === 'closed') return '3';
  if (tab === 'my') return '4';
  return '1';
};

const getTabParamFromKey = (key: string) => {
  if (key === '6') return 'callme';
  if (key === '7') return 'fromchat';
  if (key === '5') return 'unassigned';
  if (key === '2') return 'open';
  if (key === '3') return 'closed';
  if (key === '4') return 'my';
  return 'all';
};

const Tickets: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canCallMeQueue = usePermission('call_me_queue');
  const { on } = useRealtimeTicket();
  const [claimingCallMeId, setClaimingCallMeId] = useState<string | null>(null);

  const handleConvertCallMe = async (recordId: string) => {
    setClaimingCallMeId(recordId);
    try {
      await api.post(`/api/call-me/${recordId}/claim`);
      queryClient.invalidateQueries('call-me-count');
      queryClient.invalidateQueries('call-me-queue');
      navigate(`/workspace/new?fromCallMe=${recordId}`);
    } catch (e: any) {
      const code = e.response?.data?.code;
      const msg = e.response?.data?.error || 'Could not start conversion.';
      if (code === 'CALL_ME_ALREADY_CLAIMED') {
        message.warning(msg);
      } else {
        message.error(msg);
      }
      queryClient.invalidateQueries('call-me-count');
      queryClient.invalidateQueries('call-me-queue');
    } finally {
      setClaimingCallMeId(null);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') !== 'callme' || canCallMeQueue) return;
    params.set('tab', 'all');
    const qs = params.toString();
    navigate(`/tickets${qs ? `?${qs}` : ''}`, { replace: true });
  }, [location.search, canCallMeQueue, navigate]);

  // Subscribe to real-time ticket events (ticket socket connects on login)
  useEffect(() => {
    const unsubCreated = on('ticket:created', () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
    });
    const unsubUpdated = on('ticket:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
    });
    const unsubDeleted = on('ticket:deleted', () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
    });
    const unsubComment = on('ticket:comment', () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
    });
    return () => {
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
      unsubComment();
    };
  }, [on, queryClient]);

  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('priority') || '');
  const [assigneeFilter, setAssigneeFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('assignee') || '');
  const [createdByFilter, setCreatedByFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('created_by') || '');
  const [categoryFilter, setCategoryFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('category') || '');
  const [typeFilter, setTypeFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('type') || '');
  const [businessFilter, setBusinessFilter] = useState<string>(() => new URLSearchParams(window.location.search).get('business_id') || '');
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      preset: params.get('datePreset') || '',
      from: params.get('from') || undefined,
      to: params.get('to') || undefined,
    };
  });
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(window.location.search));
  const [myTickets, setMyTickets] = useState<boolean>(() => getTabFromSearch(window.location.search) === '4');
  const [pagination, setPagination] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page') || '1', 10);
    const limit = parseInt(params.get('limit') || '25', 10);
    return {
      current: Number.isFinite(page) && page > 0 ? page : 1,
      pageSize: Number.isFinite(limit) && limit > 0 ? limit : 25,
      total: 0,
    };
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [confirmBulkText, setConfirmBulkText] = useState('');

  const getParamValue = useCallback((key: string) => {
    const params = new URLSearchParams(location.search);
    return params.get(key);
  }, [location.search]);

  const clearFilterState = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
    setAssigneeFilter(activeTab === '5' ? 'unassigned' : '');
    setCreatedByFilter('');
    setCategoryFilter('');
    setTypeFilter('');
    setBusinessFilter('');
    setDateFilter({ preset: '', from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const updateQueryParams = React.useCallback(
    (updates: Record<string, string | number | null | undefined>) => {
      const params = new URLSearchParams(location.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });
      const searchString = params.toString();
      navigate(`/tickets${searchString ? `?${searchString}` : ''}`, { replace: true });
    },
    [location.search, navigate]
  );

  /** Tab key last seen from the URL — assignee/tab rules must run only when the tab changes, not when other query params (e.g. assignee) update. */
  const tabKeyFromUrlRef = React.useRef(getTabFromSearch(location.search));

  useEffect(() => {
    const tabKey = getTabFromSearch(location.search);
    if (tabKey !== activeTab) {
      setActiveTab(tabKey);
    }
    // Keep My Tickets flag in sync with URL even when activeTab already matches (avoids desync after modal/workspace flows)
    setMyTickets(tabKey === '4');

    const prevTabFromUrl = tabKeyFromUrlRef.current;
    if (tabKey !== prevTabFromUrl) {
      if (tabKey === '5' && assigneeFilter !== 'unassigned') {
        setAssigneeFilter('unassigned');
      } else if (tabKey !== '4' && tabKey !== '5' && assigneeFilter) {
        setAssigneeFilter('');
      }
      tabKeyFromUrlRef.current = tabKey;
    }

    // Sync business_id from URL
    const businessIdFromUrl = getParamValue('business_id');
    if (businessIdFromUrl !== businessFilter) {
      setBusinessFilter(businessIdFromUrl || '');
    }
    // Sync created_by from URL
    const createdByFromUrl = getParamValue('created_by');
    if (createdByFromUrl !== createdByFilter) {
      setCreatedByFilter(createdByFromUrl || '');
    }
  }, [location.search, activeTab, assigneeFilter, businessFilter, createdByFilter, getParamValue]);
  
  const handleDateFilterChange = (next: DateFilterValue) => {
    setDateFilter(next);
    setPagination(prev => ({ ...prev, current: 1 }));
    updateQueryParams({
      datePreset: next.preset || null,
      from: next.from || null,
      to: next.to || null,
      page: '1',
    });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setMyTickets(key === '4');
    setPagination(prev => ({ ...prev, current: 1 }));
    const updates: Record<string, string | number | null> = {
      tab: getTabParamFromKey(key),
      page: '1',
    };
    if (key === '5') {
      setAssigneeFilter('unassigned');
      updates.assignee = 'unassigned';
    } else if (key !== '4' && assigneeFilter) {
      setAssigneeFilter('');
      updates.assignee = null;
    }
    updateQueryParams(updates);
  };

  const handleClearFilters = () => {
    clearFilterState();
    updateQueryParams({
      search: null,
      status: null,
      priority: null,
      assignee: activeTab === '5' ? 'unassigned' : null,
      created_by: null,
      category: null,
      type: null,
      business_id: null,
      datePreset: null,
      from: null,
      to: null,
      page: '1',
    });
  };

  // Call Me queue (when on Call Me tab)
  const { data: callMeCountData } = useQuery(
    'call-me-count',
    () => api.get('/api/call-me/count').then((r) => r.data),
    { enabled: !!user && activeTab === '6' && canCallMeQueue, retry: false, refetchInterval: 60000 }
  );
  const { data: callMeQueueData } = useQuery(
    'call-me-queue',
    () => api.get('/api/call-me').then((r) => r.data),
    { enabled: !!user && activeTab === '6' && canCallMeQueue, retry: false, refetchInterval: 60000 }
  );
  const callMeCount = callMeCountData?.count ?? 0;
  const callMeItems = callMeQueueData?.items ?? [];

  const dismissCallMeMutation = useMutation(
    (id: string) => api.post(`/api/call-me/${id}/dismiss`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('call-me-count');
        queryClient.invalidateQueries('call-me-queue');
      },
    }
  );

  // Fetch tickets (disabled when on Call Me tab)
  const { data: ticketsData, isLoading, refetch } = useQuery(
    ['tickets', activeTab, searchTerm, statusFilter, priorityFilter, businessFilter, assigneeFilter, createdByFilter, categoryFilter, typeFilter, dateFilter.from, dateFilter.to, myTickets, user?.id, pagination.current, pagination.pageSize],
    async () => {
      // For Open tab, fetch both open and pending tickets separately and combine
      if (activeTab === '2' && !statusFilter) {
        try {
          const [openResponse, pendingResponse] = await Promise.all([
            api.get('/api/tickets', {
              params: {
                search: searchTerm || undefined,
                status: 'open',
                priority: priorityFilter || undefined,
                assigned_to: myTickets ? user?.id : (assigneeFilter === 'unassigned' ? 'unassigned' : assigneeFilter) || undefined,
                created_by: createdByFilter || undefined,
                category_id: categoryFilter || undefined,
                type: typeFilter || undefined,
                from: dateFilter.from || undefined,
                to: dateFilter.to || undefined,
                page: 1,
                limit: 100, // Max allowed by validation
              }
            }),
            api.get('/api/tickets', {
              params: {
                search: searchTerm || undefined,
                status: 'pending',
                priority: priorityFilter || undefined,
                assigned_to: myTickets ? user?.id : (assigneeFilter === 'unassigned' ? 'unassigned' : assigneeFilter) || undefined,
                created_by: createdByFilter || undefined,
                category_id: categoryFilter || undefined,
                type: typeFilter || undefined,
                from: dateFilter.from || undefined,
                to: dateFilter.to || undefined,
                page: 1,
                limit: 100, // Max allowed by validation
              }
            })
          ]);

          // Combine and sort by created_at desc
          const allTickets = [
            ...(openResponse.data?.tickets || []),
            ...(pendingResponse.data?.tickets || [])
          ].sort((a: any, b: any) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA;
          });

          // Calculate total (sum of both status totals)
          const openTotal = openResponse.data?.total || 0;
          const pendingTotal = pendingResponse.data?.total || 0;
          const total = openTotal + pendingTotal;

          // Apply pagination client-side
          const startIndex = (pagination.current - 1) * pagination.pageSize;
          const endIndex = startIndex + pagination.pageSize;
          const paginatedTickets = allTickets.slice(startIndex, endIndex);

          return {
            tickets: paginatedTickets,
            total: total,
            page: pagination.current,
            limit: pagination.pageSize
          };
        } catch (error: any) {
          console.error('[Tickets] Error fetching open/pending tickets:', error);
          // Fallback to empty result
          return {
            tickets: [],
            total: 0,
            page: pagination.current,
            limit: pagination.pageSize
          };
        }
      }

      // For other tabs, use normal API call
      let statusParam = statusFilter;
      if (activeTab === '3' && !statusFilter) statusParam = 'solved';
      if (activeTab === '1' || activeTab === '4') {
        statusParam = statusFilter || '';
      }

      const response = await api.get('/api/tickets', {
        params: {
          search: searchTerm || undefined,
          status: statusParam || undefined,
          priority: priorityFilter || undefined,
          assigned_to: myTickets ? user?.id : (assigneeFilter === 'unassigned' ? 'unassigned' : assigneeFilter) || undefined,
          created_by: createdByFilter || undefined,
          category_id: categoryFilter || undefined,
          type: typeFilter || undefined,
          business_id: businessFilter || undefined,
          from: dateFilter.from || undefined,
          to: dateFilter.to || undefined,
          source: activeTab === '7' ? 'chat' : undefined,
          page: pagination.current,
          limit: pagination.pageSize,
        }
      });
      return response.data;
    },
    { keepPreviousData: true, enabled: activeTab !== '6', refetchInterval: 15000 }
  );

  const tickets = ticketsData?.tickets || [];

  const locationSearch = location.search;

  // Apply client-side filters only for specific tab behaviors
  const dataForTable = React.useMemo(() => {
    let rows = tickets;
    
    // Open tab: tickets are already filtered to open + pending from API, no need to filter again
    
    // Unassigned tab — actionable tickets only (exclude solved/closed)
    if (activeTab === '5') {
      rows = rows.filter((t: any) => !t.assigned_to && t.status !== 'solved');
    }
    
    // My Tickets tab: active work assigned to current user (only if no explicit status filter).
    // Include "new" — some create paths (e.g. older modals/API) still use that status.
    if (activeTab === '4' && !statusFilter) {
      rows = rows.filter((t: any) =>
        (t.assigned_to === user?.id || t.assigned_to_id === user?.id) &&
        (t.status === 'open' || t.status === 'pending' || t.status === 'new')
      );
    }
    
    // Unassigned view or filter
    const view = new URLSearchParams(locationSearch).get('view');
    if (view === 'unassigned' || assigneeFilter === 'unassigned') {
      rows = rows.filter((t: any) => !t.assigned_to && t.status !== 'solved');
    }
    
    return rows;
  }, [tickets, activeTab, user?.id, statusFilter, assigneeFilter, locationSearch]);

  useEffect(() => {
    if (typeof ticketsData?.total === 'number') {
      setPagination(prev => ({ ...prev, total: ticketsData.total ?? 0 }));
    }
  }, [ticketsData?.total]);

  // Fetch assignable users (cache 5 min to reduce 429 rate-limit hits)
  const { data: users = [] } = useQuery(
    'assignable-users',
    async () => {
      const response = await api.get('/api/users/assignable');
      return Array.isArray(response.data) ? response.data : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const assigneeFilterSelectOptions = useMemo(() => {
    return (users as any[]).map((u: any) => {
      const displayName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      let roleDisplay = u.role_display_name;
      if (!roleDisplay) {
        const roleName = u.role
          ? String(u.role).charAt(0).toUpperCase() + String(u.role).slice(1).replace(/_/g, ' ')
          : 'User';
        roleDisplay =
          u.agent_level != null && u.agent_level !== ''
            ? `${roleName} (Level ${u.agent_level})`
            : roleName;
      }
      const searchable = `${displayName} ${roleDisplay} ${u.email || ''}`.trim();
      return { value: u.id, label: displayName, searchable };
    });
  }, [users]);

  // Fetch categories (cache 5 min)
  const { data: categoriesData } = useQuery(
    'categories',
    async () => {
      const res = await api.get('/api/categories');
      return Array.isArray(res.data?.categories) ? res.data.categories : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );
  const categories = categoriesData || [];

  // Fetch ticket types (cache 5 min)
  const { data: ticketTypesData } = useQuery(
    'ticket-types',
    async () => {
      const res = await api.get('/api/ticket-types');
      return Array.isArray(res.data?.types) ? res.data.types : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );
  const ticketTypes = ticketTypesData || [];

  // Delete mutation
  const deleteTicketMutation = useMutation(
    async (ticketId: string) => {
      await api.delete(`/api/tickets/${ticketId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
        message.success('Ticket deleted successfully');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete ticket');
      }
    }
  );

  const bulkDeleteTickets = useMutation(
    async (ids: string[]) => {
      await api.post('/api/tickets/bulk-delete', { ids });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tickets'], exact: false });
        message.success('Selected tickets deleted');
        setSelectedRowKeys([]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to bulk delete tickets');
      }
    }
  );

  const getStatusColor = (status: string) => {
    const colors: any = {
      open: 'blue',
      pending: 'orange',
      solved: 'green',
      new: 'default',
    };
    return colors[status] || 'default';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'technical': 'blue',
      'billing': 'green',
      'general': 'purple',
      'hardware': 'orange',
      'software': 'cyan',
      'network': 'magenta',
      'other': 'default',
    };
    return colors[category?.toLowerCase()] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'default',
      medium: 'blue',
      high: 'orange',
    };
    return colors[priority] || 'default';
  };

  const columns: ColumnsType<Ticket> = [
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 110,
      render: (text, record) => (
        <Tooltip title="Click to open in new tab">
          <Button
            type="link"
            onClick={(e) => {
              navigate(`/workspace/number/${record.ticket_number}`, {
                state: { openInNewTab: true },
              });
            }}
            style={{ padding: 0, fontFamily: 'monospace' }}
          >
            #{text}
          </Button>
        </Tooltip>
      ),
    },
      {
        title: 'Subject',
        dataIndex: 'subject',
        key: 'subject',
        ellipsis: true,
        width: 300,
        render: (text, record) => (
          <div
            style={{ marginLeft: 8, cursor: 'pointer' }}
            onClick={(e) => {
              navigate(`/workspace/number/${record.ticket_number}`, {
                state: { openInNewTab: true },
              });
            }}
          >
            <div style={{ fontWeight: 500 }}>{text}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {(() => {
                const businessName = record.business_name || 'N/A';
                const mid = (record as any).mid;
                const address = (record as any).address;
                const city = (record as any).city;
                const state = (record as any).state;
                const zip = (record as any).zip_code;
                
                const businessLine = [businessName];
                if (mid) businessLine.push(`MID: ${mid}`);
                
                // Build full address on separate line
                const addressParts = [address, city, state, zip].filter(Boolean);
                const addressLine = addressParts.length > 0 ? addressParts.join(', ') : '';
                
                return (
                  <div>
                    <div>{businessLine.join(' • ')}</div>
                    {addressLine && <div style={{ marginTop: 2 }}>{addressLine}</div>}
                  </div>
                );
              })()}
            </div>
          </div>
        ),
      },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category',
      width: 150,
      render: (category) => (
        <Tag color={getCategoryColor(category)}>
          {category ? category.toUpperCase() : 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Created By',
      key: 'created_by',
      width: 140,
      render: (_, record) => {
        const first = (record as any).created_by_name;
        const last = (record as any).created_by_last_name || '';
        const name = first ? `${first} ${last}`.trim() : '—';
        const initial = first?.[0] || '—';
        return (
          <Space>
            <Avatar size="small">{initial}</Avatar>
            <span>{name}</span>
          </Space>
        );
      },
    },
    {
      title: 'Assignee',
      key: 'assignee',
      width: 150,
      render: (_, record) => {
        const first = (record as any).assigned_to_first_name || (record as any).assigned_to_name || (record as any).assignee_first_name;
        const last = (record as any).assigned_to_last_name || (record as any).assignee_last_name || '';
        const name = first ? `${first} ${last}`.trim() : 'Unassigned';
        const initial = first?.[0] || 'U';
        
        return (
          <Space>
            <Avatar size="small">{initial}</Avatar>
            <span>{name}</span>
          </Space>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => (
        <Tooltip title={dayjs(date).format('MMMM D, YYYY h:mm A')}>
          <span>{dayjs(date).fromNow()}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Last Update',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 140,
      render: (date) => (
        <Tooltip title={dayjs(date).format('MMMM D, YYYY h:mm A')}>
          <span>{dayjs(date).fromNow()}</span>
        </Tooltip>
      ),
    },
  ];

  const isCompanyScopedPartner =
    user?.role === 'agent_partner' || user?.role === 'sales_rep_associated';

  const tabItems = [
    { key: '1', label: isCompanyScopedPartner ? 'Company tickets' : 'All', children: null },
    ...(canCallMeQueue
      ? [
          {
            key: '6',
            label: (
              <span>
                Call Me
                {callMeCount > 0 && <Badge count={callMeCount} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />}
              </span>
            ),
            children: null,
          },
        ]
      : []),
    { key: '5', label: 'Unassigned', children: null },
    { key: '2', label: 'Open', children: null },
    { key: '3', label: 'Closed', children: null },
    { key: '4', label: isCompanyScopedPartner ? 'Assigned to me' : 'My Tickets', children: null },
  ];

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Tickets</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/workspace/new')}>
            New Ticket
          </Button>
        </Space>
      </div>

      {/* Search and Filters - hidden on Call Me tab */}
      {activeTab !== '6' && (
      <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 8 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Space wrap>
          <Select
            placeholder="Status"
            value={statusFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setStatusFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ status: normalized || null, page: '1' });
            }}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="open">Open</Option>
            <Option value="pending">Pending</Option>
            <Option value="solved">Solved</Option>
          </Select>
          <Select
            placeholder="Priority"
            value={priorityFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setPriorityFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ priority: normalized || null, page: '1' });
            }}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>
          <Select
            placeholder="Assignee"
            value={assigneeFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setAssigneeFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ assignee: normalized || null, page: '1' });
            }}
            style={{ width: 220 }}
            allowClear
            disabled={myTickets}
            showSearch
            options={[
              { value: 'unassigned', label: 'Unassigned', searchable: 'unassigned' },
              ...assigneeFilterSelectOptions,
            ]}
            filterOption={(input, option: any) => {
              const searchText = (input || '').toLowerCase().trim();
              if (!searchText) return true;
              const searchable = (option?.searchable ?? option?.label ?? '').toLowerCase();
              return searchable.includes(searchText);
            }}
            dropdownStyle={{ minWidth: 280 }}
            dropdownMatchSelectWidth={false}
            optionRender={(optOrPayload: any) => {
              const opt = optOrPayload?.option ?? optOrPayload;
              const val = opt?.value;
              if (val === 'unassigned') return <span>Unassigned</span>;
              const u = (users as any[]).find((x: any) => String(x.id) === String(val));
              if (!u) return <span>{opt?.label ?? ''}</span>;
              let roleDisplay = u.role_display_name;
              if (!roleDisplay) {
                const roleName = u.role
                  ? String(u.role).charAt(0).toUpperCase() + String(u.role).slice(1).replace(/_/g, ' ')
                  : 'User';
                roleDisplay =
                  u.agent_level != null && u.agent_level !== ''
                    ? `${roleName} (Level ${u.agent_level})`
                    : roleName;
              }
              const displayName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
              return (
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', overflow: 'hidden', padding: '2px 0' }}>
                  <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                  <span style={{ fontSize: 12, color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleDisplay}</span>
                </div>
              );
            }}
          />
          <Select
            placeholder="Created By"
            value={createdByFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setCreatedByFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ created_by: normalized || null, page: '1' });
            }}
            style={{ width: 200 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const u = users.find((x: any) => x.id === option?.value);
              if (u) {
                const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
                return fullName.includes(input.toLowerCase());
              }
              return false;
            }}
            optionFilterProp="children"
          >
            {users.map((u: any) => (
              <Option key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Category"
            value={categoryFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setCategoryFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ category: normalized || null, page: '1' });
            }}
            style={{ width: 150 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {categories.map((cat: any) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Type"
            value={typeFilter || undefined}
            onChange={(value) => {
              const normalized = value || '';
              setTypeFilter(normalized);
              setPagination(prev => ({ ...prev, current: 1 }));
              updateQueryParams({ type: normalized || null, page: '1' });
            }}
            style={{ width: 150 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {ticketTypes.filter((t: any) => t.is_active !== false).map((type: any) => (
              <Option key={type.id} value={type.name}>
                {type.name}
              </Option>
            ))}
          </Select>
          <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
          </Space>
          <Button type="link" onClick={handleClearFilters}>
            Clear filters
          </Button>
        </Space>
      </div>
      )}

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} style={{ marginBottom: 16 }} />

      {/* Call Me Queue - when on Call Me tab */}
      {activeTab === '6' && (
        <Card
          title={
            <Space>
              <PhoneOutlined />
              <Text strong>Call Me Queue</Text>
              {callMeCount > 0 && (
                <Badge count={callMeCount} style={{ backgroundColor: '#faad14' }} />
              )}
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          {callMeItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
              <Text type="secondary">No pending Call Me requests</Text>
            </div>
          ) : (
            <Table
              dataSource={callMeItems}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                { title: 'Store ID', dataIndex: 'store_id', key: 'store_id', width: 120, render: (t: string) => <Typography.Text>{t || '—'}</Typography.Text> },
                { title: 'Business', dataIndex: 'business_name', key: 'business_name', ellipsis: true, render: (t: string) => <Typography.Text>{t || '—'}</Typography.Text> },
                { title: 'Service', dataIndex: 'service_type', key: 'service_type', width: 120, render: (t: string) => <Tag>{t || '—'}</Tag> },
                { title: 'Notes', dataIndex: 'notes', key: 'notes', ellipsis: true, render: (t: string) => <Typography.Text type="secondary">{t ? `${t.slice(0, 60)}${t.length > 60 ? '…' : ''}` : '—'}</Typography.Text> },
                { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 100, render: (d: string) => dayjs(d).format('MMM D, h:mm A') },
                {
                  title: '',
                  key: 'actions',
                  width: 180,
                  render: (_: any, record: any) => {
                    const isOwnConverting =
                      record.status === 'converting' &&
                      !!user?.id &&
                      String(record.claimed_by) === String(user.id);
                    return (
                      <Space direction="vertical" size={4}>
                        {isOwnConverting && (
                          <Tag color="processing" style={{ marginRight: 0 }}>
                            Converting (you)
                          </Tag>
                        )}
                        <Button
                          type="primary"
                          size="small"
                          loading={claimingCallMeId === record.id}
                          onClick={() => handleConvertCallMe(record.id)}
                        >
                          {isOwnConverting ? 'Resume conversion' : 'Convert to ticket'}
                        </Button>
                        <Button size="small" danger icon={<CloseOutlined />} loading={dismissCallMeMutation.isLoading} onClick={() => dismissCallMeMutation.mutate(record.id)}>
                          Dismiss
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
            />
          )}
        </Card>
      )}

      {/* Actions bar when ticket(s) selected */}
      {activeTab !== '6' && selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#e6f7ff', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Typography.Text strong>
            {selectedRowKeys.length} ticket{selectedRowKeys.length !== 1 ? 's' : ''} selected
          </Typography.Text>
          <Space wrap>
            {selectedRowKeys.length === 1 && (() => {
              const selectedTicket = dataForTable.find((t: any) => t.id === selectedRowKeys[0]);
              return selectedTicket ? (
                <Button
                  type="primary"
                  icon={<ExportOutlined />}
                  onClick={() => navigate(`/workspace/number/${selectedTicket.ticket_number}`, { state: { openInNewTab: true } })}
                >
                  Open ticket
                </Button>
              ) : null;
            })()}
            {user?.role === 'admin' && (
              selectedRowKeys.length === 1 ? (
                <Popconfirm
                  title="Delete ticket"
                  description="Are you sure you want to delete this ticket?"
                  onConfirm={() => deleteTicketMutation.mutate(selectedRowKeys[0] as string)}
                >
                  <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              ) : (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => { setConfirmBulkText(''); setConfirmBulkOpen(true); }}
                >
                  Bulk Delete
                </Button>
              )
            )}
            <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ padding: 0 }}>
              Clear selection
            </Button>
          </Space>
        </div>
      )}

      {/* Tickets Table - hidden on Call Me tab */}
      {activeTab !== '6' && (
      <Table
                  columns={columns}
        dataSource={dataForTable}
        rowKey="id"
        loading={isLoading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} tickets`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
            updateQueryParams({ page: String(page), limit: String(pageSize) });
          },
        }}
        scroll={{ x: 1200 }}
      />
      )}

      <Modal
        title={`Confirm Bulk Delete (${selectedRowKeys.length} ticket(s))`}
        open={confirmBulkOpen}
        onCancel={() => setConfirmBulkOpen(false)}
        onOk={() => { bulkDeleteTickets.mutate(selectedRowKeys as string[]); setConfirmBulkOpen(false); }}
        okButtonProps={{ danger: true, disabled: confirmBulkText !== 'DELETE', children: 'Delete' }}
      >
        <Typography.Paragraph type="secondary">
          This action cannot be undone. Type DELETE to confirm.
        </Typography.Paragraph>
        <Input placeholder="Type DELETE" value={confirmBulkText} onChange={(e) => setConfirmBulkText(e.target.value)} />
      </Modal>
    </div>
  );
};

export default Tickets;
